import { findIndex, orderBy } from 'lodash';
import React, {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import { makeStyles } from '@material-ui/core/styles';
import {
  EvidenceContext,
  FormPanel,
  TableFilter,
  useEventCallback,
} from '@eas/common-web';
import type { FilterLayoutProps } from './filter';
import { FilterComponent } from './filter-component';
import { FilterState } from './filter-types';
import { isFilterValueActive } from './filter-utils';

const useStyles = makeStyles(() => ({
  filterColumn: {
    flex: '1 1 250px',
    backgroundColor: '#f1f3f4',
    paddingBottom: 10,
  },
  filterGrid: {
    display: 'grid',
    gap: 10,
    minWidth: 0,
    width: '100%',
  },
  addButton: {
    margin: 10,
    boxShadow: '0 0 0',
    // width: 32,
    height: '32px !important',
    minHeight: '32px !important',
    padding: '0 15px !important',

    '&:active': {
      boxShadow: '0 0 0',
    },
  },
  addButtonIcon: {
    width: 20,
    height: 20,
    marginRight: 10,
  },
}));

export function FilterColumn({
  filterGroup,
  filters,
  setFilters,
  label,
  initVisibility,
  layout,
}: {
  filterGroup: TableFilter[];
  filters: FilterState[];
  setFilters: Dispatch<SetStateAction<FilterState[]>>;
  label: string;
  initVisibility?: string[];
  layout?: FilterLayoutProps;
}) {
  const classes = useStyles();
  const { tableRef } = useContext(EvidenceContext);
  const orderedFilters = orderBy(filterGroup, 'filterOrder');

  const [visibleFilters, setVisibleFilters] = useState<{
    [key: string]: boolean;
  }>({});

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleShow = (filter: TableFilter) => {
    setVisibleFilters((rest) => ({
      ...rest,
      [filter.datakey]: !visibleFilters[filter.datakey],
    }));
  };

  const showVisibleFilters = useEventCallback((initVisibility?: string[]) => {
    if (initVisibility && initVisibility?.length > 0) {
      const init = initVisibility.reduce((a, v) => ({ ...a, [v]: true }), {});

      setVisibleFilters(init);
    } else {
      const appliedFilters = filters.filter(isFilterValueActive);
      const init = appliedFilters.reduce(
        (a, v) => ({ ...a, [v.id!]: true }),
        visibleFilters
      );

      setVisibleFilters(init);
    }
  });

  /**
   * Changes the value of the filter.
   */
  const handleFilterValueChange = useEventCallback(
    (index: number, value: any) => {
      setFilters((filters) => [
        ...filters.slice(0, index),
        { ...filters[index], visible: true, value },
        ...filters.slice(index + 1),
      ]);
    }
  );

  /**
   * Changes the state of the filter.
   */
  const handleFilterStateChange = useEventCallback(
    (index: number, state: FilterState) => {
      setFilters((filters) => [
        ...filters.slice(0, index),
        state,
        ...filters.slice(index + 1),
      ]);
    }
  );

  useEffect(() => {
    showVisibleFilters();
  }, [filters]);

  useEffect(() => {
    showVisibleFilters(initVisibility);
  }, []);

  const renderedFilters = orderedFilters.map((filter) => {
    const index = findIndex(
      tableRef.current?.filters,
      (f) => f.datakey === filter.datakey
    );
    const state = filters[index];

    return (
      <FilterComponent
        key={index}
        filter={filter}
        state={state ?? {}}
        onChangeValue={(value) => handleFilterValueChange(index, value)}
        onChangeFilterState={(state) => handleFilterStateChange(index, state)}
        visible={visibleFilters[filter.datakey]}
      />
    );
  });

  const gridColumns = layout?.columns ?? 3;
  const minColumnWidth = layout?.minColumnWidth ?? 250;
  const gridTemplateColumns =
    gridColumns === 'auto'
      ? `repeat(auto-fit, minmax(${minColumnWidth}px, 1fr))`
      : `repeat(${gridColumns}, minmax(0, 1fr))`;

  return (
    <div
      className={classes.filterColumn}
      style={layout?.type === 'grid' ? { flex: '1 1 100%' } : undefined}
    >
      <FormPanel label={label} bottomBorder={false}>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          elevation={3}
          variant={'menu'}
        >
          {orderedFilters.map((filter) => (
            <MenuItem
              key={filter.datakey}
              onClick={() => handleShow(filter)}
              selected={visibleFilters[filter.datakey]}
            >
              {filter.label}
            </MenuItem>
          ))}
        </Menu>
        {layout?.type === 'grid' ? (
          <div className={classes.filterGrid} style={{ gridTemplateColumns }}>
            {renderedFilters}
          </div>
        ) : (
          renderedFilters
        )}
      </FormPanel>
    </div>
  );
}
